import { IListingStatsResponse } from "../../models/Part";
import { FirstListedBucket } from "../../models/listingOptimisation/IListingInsights";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import LoadingPulse from "../shared/LoadingPulse";
import { IQueueFilters } from "./queueFilterState";

interface ITriageTilesProps {
  stats: IListingStatsResponse | undefined;
  isLoading: boolean;
  filters: IQueueFilters;
  onChange: (next: Partial<IQueueFilters>) => void;
}

interface ITile {
  key: string;
  label: string;
  value: number | undefined;
  caption: string;
  valueTone: string;
  active: boolean;
  onClick: () => void;
}

const formatCount = (value: number | undefined): string => (value == null ? "—" : value.toLocaleString("en-GB"));

/**
 * Six counts from the listing stats that each narrow the queue when clicked: the four first-listed
 * age buckets, listings priced above intelligence, and listings with no part numbers. Clicking an
 * active tile clears that filter again.
 */
export default function TriageTiles({ stats, isLoading, filters, onChange }: ITriageTilesProps) {
  const toggleBucket = (bucket: FirstListedBucket) =>
    onChange({ firstListedBucket: filters.firstListedBucket === bucket ? null : bucket });

  const bucketTile = (
    bucket: FirstListedBucket,
    label: string,
    value: number | undefined,
    caption: string,
    valueTone: string
  ): ITile => ({
    key: bucket,
    label,
    value,
    caption,
    valueTone,
    active: filters.firstListedBucket === bucket,
    onClick: () => toggleBucket(bucket),
  });

  const over180Caption =
    stats?.firstListedOver180TotalPrice != null && stats.firstListedOver180TotalPrice > 0
      ? `${PriceFormat(stats.firstListedOver180TotalPrice)} listed`
      : "The long tail";

  const tiles: ITile[] = [
    bucketTile("under30", "First listed < 30d", stats?.firstListedUnder30, "Fresh — leave alone", "text-green-700"),
    bucketTile("d30to90", "30 – 90d", stats?.firstListed30To90, "Check title and price", "text-amber-700"),
    bucketTile("d90to180", "90 – 180d", stats?.firstListed90To180, "Reprice against sold", "text-red-700"),
    bucketTile("over180", "180d +", stats?.firstListedOver180, over180Caption, "text-red-700"),
    {
      key: "pricedAboveIntelligence",
      label: "Priced above intelligence",
      value: stats?.pricedAboveIntelligence,
      caption: "Live > intelligence avg + 10%",
      valueTone: "text-amber-700",
      active: filters.pricedAboveIntelligence,
      onClick: () => onChange({ pricedAboveIntelligence: !filters.pricedAboveIntelligence }),
    },
    {
      key: "missingPartNumbers",
      label: "No part numbers",
      value: stats?.missingPartNumbers,
      caption: "Missing MPN and OE number",
      valueTone: "text-slate-800",
      active: filters.missingPartNumbers,
      onClick: () => onChange({ missingPartNumbers: !filters.missingPartNumbers }),
    },
  ];

  return (
    <section aria-label="Triage" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {tiles.map((tile) => (
        <button
          key={tile.key}
          type="button"
          onClick={tile.onClick}
          aria-pressed={tile.active}
          className={classNames(
            "flex flex-col gap-0.5 rounded-xl bg-white px-3.5 py-3 text-left shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/40",
            tile.active ? "border-2 border-primary-600" : "border border-gray-200"
          )}
        >
          <span
            className={classNames(
              "text-[11px] font-semibold uppercase tracking-wide",
              tile.active ? "text-primary-700" : "text-gray-500"
            )}
          >
            {tile.label}
          </span>
          {isLoading && !stats ? (
            <span className="py-1">
              <LoadingPulse />
            </span>
          ) : (
            <span className={classNames("text-[22px] font-semibold leading-8 tabular-nums", tile.valueTone)}>
              {formatCount(tile.value)}
            </span>
          )}
          <span className="text-xs text-gray-500">
            {tile.caption}
            {tile.active ? " · showing" : ""}
          </span>
        </button>
      ))}
    </section>
  );
}
